import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SeoMeta from "../seo/SeoMeta";
import LearnShell from "./LearnShell";
import { findLearnThemeOfSlug } from "../../utils/learnThemes";
import { LEARN_DIAGRAMS, OFFICIAL_RULES_ES, getLearnSeoPage } from "../../content/learnPages";
import {
  LEARN_OG_IMAGE,
  SITE_ORIGIN,
  canonicalLearnUrl,
  isPublicLearnPathname,
  resolveLearnPathForMode,
} from "../../utils/learnRoutes";

const XIMA_PLAY_URL = "https://juegoxima.com/";

const UI_TEXT = {
  es: {
    home: "Aprender",
    kicker: "Guía XIMA",
    back: "Volver a",
    play: "🎲 Jugar gratis",
    updated: "Actualizado",
    steps: "Pasos",
    faqs: "Preguntas frecuentes",
    previous: "‹ Guía anterior",
    next: "Siguiente guía ›",
    nextStep: "Siguiente paso",
    nextStepText: "Continúa aprendiendo con esta guía.",
    related: "Guías relacionadas",
    facts: "Ficha del juego",
    video: "Video",
    videoPlay: "Ver el video",
    rules: "Las 16 reglas oficiales",
    otherLanguage: "🌐 Read this guide in English",
    of: "de",
  },
  en: {
    home: "Learn XIMA",
    kicker: "XIMA guide",
    back: "Back to",
    play: "🎲 Play for free",
    updated: "Updated",
    steps: "Steps",
    faqs: "Frequently asked questions",
    previous: "‹ Previous guide",
    next: "Next guide ›",
    nextStep: "Next step",
    nextStepText: "Keep learning with this guide.",
    related: "Related guides",
    facts: "Fact sheet",
    video: "Video",
    videoPlay: "Watch the video",
    rules: "The official rules",
    otherLanguage: "🌐 Leer esta guía en español",
    of: "of",
  },
  ru: {
    home: "XIMA на русском",
    kicker: "Руководство XIMA",
    back: "Назад:",
    play: "🎲 Играть бесплатно",
    updated: "Обновлено",
    steps: "Шаги",
    faqs: "Частые вопросы",
    previous: "‹ Предыдущее",
    next: "Следующее ›",
    nextStep: "Дальше",
    nextStepText: "Продолжайте учиться с этим руководством.",
    related: "Похожие руководства",
    facts: "Об игре",
    video: "Видео",
    videoPlay: "Смотреть видео",
    rules: "Официальные правила",
    otherLanguage: "🌐 Другие языки",
    of: "из",
  },
  de: {
    home: "XIMA auf Deutsch",
    kicker: "XIMA-Anleitung",
    back: "Zurück zu",
    play: "🎲 Kostenlos spielen",
    updated: "Aktualisiert",
    steps: "Schritte",
    faqs: "Häufige Fragen",
    previous: "‹ Vorherige Anleitung",
    next: "Nächste Anleitung ›",
    nextStep: "Weiter",
    nextStepText: "Lerne mit dieser Anleitung weiter.",
    related: "Verwandte Anleitungen",
    facts: "Steckbrief",
    video: "Video",
    videoPlay: "Video ansehen",
    rules: "Die offiziellen Regeln",
    otherLanguage: "🌐 Andere Sprachen",
    of: "von",
  },
};

const LANGUAGE_NAMES = { es: "Español", en: "English", ru: "Русский", de: "Deutsch" };
const LANGUAGE_HUBS = ["en", "ru", "de"];

const absoluteUrl = (path) => {
  const value = String(path || "");
  if (/^https?:\/\//i.test(value)) return value;
  return `${SITE_ORIGIN}${value.replace(/^\/game\/learn/, "/aprender")}`;
};

export const buildStructuredData = (page) => {
  if (!page) return null;
  const lang = page.lang || "es";
  const figureImages = (page.sections || [])
    .map((section) => LEARN_DIAGRAMS[section.figure])
    .filter(Boolean)
    .map((diagram) => `${SITE_ORIGIN}${diagram.src}`);

  const article = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: page.h1,
    name: page.title,
    description: page.metaDescription,
    url: page.canonicalUrl,
    inLanguage: lang,
    image: [page.ogImage || LEARN_OG_IMAGE, ...figureImages],
    datePublished: page.publishedAt || page.updatedAt,
    dateModified: page.updatedAt,
    mainEntityOfPage: page.canonicalUrl,
    author: { "@type": "Organization", name: "XIMA", url: SITE_ORIGIN },
    publisher: {
      "@type": "Organization",
      name: "XIMA",
      url: SITE_ORIGIN,
      logo: { "@type": "ImageObject", url: `${SITE_ORIGIN}/logo512.png` },
    },
    about: { "@type": "Game", name: "XIMA", url: SITE_ORIGIN },
  };

  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      ...page.breadcrumbs.map((crumb, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: crumb.label,
        item: absoluteUrl(crumb.path),
      })),
      {
        "@type": "ListItem",
        position: page.breadcrumbs.length + 1,
        name: page.h1,
        item: page.canonicalUrl,
      },
    ],
  };

  const faq =
    page.faqs?.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          inLanguage: lang,
          mainEntity: page.faqs.map((item) => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: { "@type": "Answer", text: item.answer },
          })),
        }
      : null;

  return faq ? [article, breadcrumb, faq] : [article, breadcrumb];
};

// Se conserva para quien lo importe: las guías nuevas ya traen sus propios pasos.
export const buildFallbackSteps = () => [];

export const buildAlternateLinks = (page) => {
  if (!page) return [];
  const spanishHome = `${SITE_ORIGIN}/aprender`;
  if (LANGUAGE_HUBS.includes(page.slug)) {
    return [
      { hrefLang: "es", href: spanishHome },
      ...LANGUAGE_HUBS.map((hub) => ({ hrefLang: hub, href: canonicalLearnUrl(hub) })),
      { hrefLang: "x-default", href: spanishHome },
    ];
  }
  const translations = (page.translations || []).map((item) => getLearnSeoPage(item.slug)).filter(Boolean);
  if (translations.length < 2) return [];
  const spanish = translations.find((item) => item.lang === "es") || page;
  return [
    ...translations.map((item) => ({ hrefLang: item.lang, href: item.canonicalUrl })),
    { hrefLang: "x-default", href: spanish.canonicalUrl },
  ];
};

const Paragraphs = ({ items }) =>
  (Array.isArray(items) ? items : items ? [items] : []).map((text, index) => (
    <p key={index} className="xle-prose-p">
      {text}
    </p>
  ));

const Diagram = ({ id, lang }) => {
  const diagram = LEARN_DIAGRAMS[id];
  if (!diagram) return null;
  return (
    <figure className="xle-figure">
      <img
        src={diagram.src}
        alt={diagram.alt[lang] || diagram.alt.es}
        width={diagram.width}
        height={diagram.height}
        loading="lazy"
        decoding="async"
      />
      <figcaption>{diagram.caption[lang] || diagram.caption.es}</figcaption>
    </figure>
  );
};

const GuideSection = ({ section, lang, renderLink }) => (
  <section className="xle-card xle-prose">
    <h2>{section.heading}</h2>
    <Paragraphs items={section.p} />
    {section.list?.length ? (
      <ul className="xle-prose-list">
        {section.list.map((item, index) => (
          <li key={index}>{item}</li>
        ))}
      </ul>
    ) : null}
    {section.defs?.length ? (
      <dl className="xle-defs">
        {section.defs.map(([term, text]) => (
          <div key={term}>
            <dt>{term}</dt>
            <dd>{text}</dd>
          </div>
        ))}
      </dl>
    ) : null}
    {section.table ? (
      <div className="xle-table-wrap">
        <table className="xle-table">
          <thead>
            <tr>
              {section.table.head.map((cell, index) => (
                <th key={index} scope="col">
                  {cell}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {section.table.rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, cellIndex) =>
                  cellIndex === 0 ? (
                    <th key={cellIndex} scope="row">
                      {cell}
                    </th>
                  ) : (
                    <td key={cellIndex}>{cell}</td>
                  )
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : null}
    {section.qa?.length ? (
      <div className="xle-faqs">
        {section.qa.map(([question, answer]) => (
          <details key={question} open>
            <summary>
              <span aria-hidden="true">❓</span>
              <span>{question}</span>
            </summary>
            <p>{answer}</p>
          </details>
        ))}
      </div>
    ) : null}
    <Paragraphs items={section.after} />
    {section.links?.length ? <div className="xle-links xle-links-inline">{section.links.map(renderLink)}</div> : null}
    {section.figure ? <Diagram id={section.figure} lang={lang} /> : null}
  </section>
);

const GuideVideo = ({ youtubeId, title, text }) => {
  const [playing, setPlaying] = useState(false);
  if (!youtubeId) return null;
  return (
    <section className="xle-card xle-guide-video">
      <h2>{text.video}</h2>
      {playing ? (
        <div className="xle-video-frame">
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1&rel=0`}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      ) : (
        <a
          className="xle-video-frame xle-video-thumb"
          href={`https://www.youtube.com/watch?v=${youtubeId}`}
          onClick={(event) => {
            event.preventDefault();
            setPlaying(true);
          }}
          style={{ backgroundImage: `url(https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg)` }}
          aria-label={`${text.videoPlay}: ${title}`}
        >
          <span aria-hidden="true">▶</span>
        </a>
      )}
    </section>
  );
};

const LearnSeoPage = ({ page, publicMode = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const structuredData = useMemo(() => buildStructuredData(page), [page]);
  const alternateLinks = useMemo(() => buildAlternateLinks(page), [page]);
  const isPublicRoute = publicMode || isPublicLearnPathname(location.pathname);
  const theme = useMemo(() => findLearnThemeOfSlug(page?.slug), [page]);
  const lang = UI_TEXT[page?.lang] ? page.lang : "es";
  const text = UI_TEXT[lang];

  useEffect(() => {
    // Al pasar de una guía a otra se empieza arriba.
    document.querySelectorAll(".xle-main, .xima-mobile-scroll-main, .xima-mobile-scroll-page").forEach((scroller) => {
      scroller.scrollTop = 0;
    });
    window.scrollTo(0, 0);
  }, [page]);

  if (!page) return null;

  const goTo = (path) => {
    if (!path) return;
    if (/^https?:\/\//i.test(path)) {
      window.open(path, "_blank", "noopener,noreferrer");
      return;
    }
    const targetPath = resolveLearnPathForMode(path, isPublicRoute);
    if (targetPath.startsWith("/#")) {
      window.location.assign(targetPath);
      return;
    }
    navigate(targetPath);
  };
  const hrefFor = (path) => resolveLearnPathForMode(path, isPublicRoute);
  const handleAnchorNavigation = (event, path) => {
    event.preventDefault();
    goTo(path);
  };

  // Volver a Aprender abre la pestaña Guías en el tema de esta guía.
  const learnBase = isPublicRoute ? "/aprender" : "/game/learn";
  const themeQuery = `?tab=guias${theme ? `&tema=${theme.id}` : ""}`;
  const isEnglishHub = LANGUAGE_HUBS.includes(page.slug);
  const themeHomePath = isEnglishHub ? learnBase : lang !== "es" ? `${learnBase}/${lang}` : `${learnBase}${themeQuery}`;
  const themeHomeHref = isPublicRoute ? themeHomePath : `#${themeHomePath}`;
  const goToThemeHome = (event) => {
    event.preventDefault();
    navigate(themeHomePath);
  };

  const themeGuides = theme?.guides || [];
  const indexInTheme = themeGuides.findIndex((guide) => guide.slug === page.slug);
  const previousGuide = indexInTheme > 0 ? themeGuides[indexInTheme - 1] : null;
  const nextGuide =
    indexInTheme >= 0 && indexInTheme < themeGuides.length - 1 ? themeGuides[indexInTheme + 1] : null;

  const otherLanguagePages = LANGUAGE_HUBS.includes(page.slug)
    ? [{ lang: "es", path: "/game/learn", navLabel: "Aprender" }, ...LANGUAGE_HUBS.filter((hub) => hub !== page.slug).map((hub) => getLearnSeoPage(hub))]
    : (page.translations || []).filter((item) => item.slug !== page.slug).map((item) => getLearnSeoPage(item.slug));
  const languageLinks = otherLanguagePages.filter(Boolean);
  const backLabel = isEnglishHub ? "Aprender (español)" : lang !== "es" ? text.home : theme ? theme.label : text.home;

  const renderGuideLink = (slug) => {
    const target = getLearnSeoPage(slug);
    if (!target) return null;
    return (
      <a key={slug} href={hrefFor(target.path)} onClick={(event) => handleAnchorNavigation(event, target.path)}>
        <span>
          <strong>{target.navLabel}</strong>
          <small>{target.metaDescription}</small>
        </span>
        <span aria-hidden="true">›</span>
      </a>
    );
  };

  return (
    <LearnShell publicMode={isPublicRoute}>
      <SeoMeta
        title={page.title}
        description={page.metaDescription}
        canonicalUrl={page.canonicalUrl}
        ogTitle={page.ogTitle}
        ogDescription={page.ogDescription}
        ogImage={page.ogImage || LEARN_OG_IMAGE}
        robots={page.robots || "index,follow"}
        datePublished={page.publishedAt || page.updatedAt}
        dateModified={page.updatedAt}
        structuredData={structuredData}
        alternateLinks={alternateLinks}
      />

      <nav className="xle-crumbs" aria-label="Breadcrumb">
        <a href={themeHomeHref} onClick={goToThemeHome}>
          📖 {text.home}
        </a>
        {theme && lang === "es" ? (
          <>
            <span>›</span>
            <a href={themeHomeHref} onClick={goToThemeHome}>
              {theme.icon} {theme.label}
            </a>
          </>
        ) : null}
        <span>›</span>
        <b aria-current="page">{page.h1}</b>
      </nav>

      <header className="xle-card xle-guide-hero">
        <div className="xle-guide-hero-text">
          <span className="xle-kicker">
            {text.kicker}
            {theme && indexInTheme >= 0 ? ` · ${theme.label} · ${indexInTheme + 1} ${text.of} ${themeGuides.length}` : ""}
          </span>
          <h1>{page.h1}</h1>
          <p>{page.intro}</p>
          <div className="xle-row-buttons">
            <a href={themeHomeHref} onClick={goToThemeHome} className="xle-btn">
              ‹ {text.back} {backLabel}
            </a>
            {page.cta ? (
              <a
                href={hrefFor(page.cta.path)}
                onClick={(event) => handleAnchorNavigation(event, page.cta.path)}
                className="xle-btn is-go"
              >
                {page.cta.label} ›
              </a>
            ) : null}
            {isPublicRoute ? (
              <a href={XIMA_PLAY_URL} className="xle-btn is-go" aria-label="Jugar XIMA">
                {text.play}
              </a>
            ) : null}
          </div>
        </div>
        <div className="xle-guide-meta">
          <span>
            📅 {text.updated}: <time dateTime={page.updatedAt}>{page.updatedAt}</time>
          </span>
          {languageLinks.length > 0 ? (
            <span className="xle-lang-links">
              {languageLinks.map((item) => (
                <a
                  key={item.lang}
                  href={hrefFor(item.path)}
                  onClick={(event) => handleAnchorNavigation(event, item.path)}
                  hrefLang={item.lang}
                  lang={item.lang}
                >
                  🌐 {LANGUAGE_NAMES[item.lang] || item.lang}
                </a>
              ))}
            </span>
          ) : null}
        </div>
      </header>

      <div className="xle-guide-layout">
        <article className="xle-guide-article" lang={lang}>
          {page.sections.map((section, index) => (
            <GuideSection key={`${section.heading}-${index}`} section={section} lang={lang} renderLink={renderGuideLink} />
          ))}

          {page.rulesList === "es" ? (
            <section className="xle-card">
              <h2>{text.rules}</h2>
              <ol className="xle-rule-list">
                {OFFICIAL_RULES_ES.map((rule, index) => (
                  <li key={index}>
                    <span>{index + 1}</span>
                    <p>{rule}</p>
                  </li>
                ))}
              </ol>
            </section>
          ) : null}

          {page.steps.length > 0 ? (
            <section className="xle-card">
              <h2>{page.stepsTitle || text.steps}</h2>
              <ol className="xle-rule-list">
                {page.steps.map((step, index) => (
                  <li key={`${step}-${index}`}>
                    <span>{index + 1}</span>
                    <p>{step}</p>
                  </li>
                ))}
              </ol>
            </section>
          ) : null}

          <GuideVideo youtubeId={page.video} title={page.h1} text={text} />

          {page.faqs.length > 0 ? (
            <section className="xle-card">
              <h2>{text.faqs}</h2>
              <div className="xle-faqs">
                {page.faqs.map((faq) => (
                  <details key={faq.question}>
                    <summary>
                      <span aria-hidden="true">❓</span>
                      <span>{faq.question}</span>
                    </summary>
                    <p>{faq.answer}</p>
                  </details>
                ))}
              </div>
            </section>
          ) : null}

          {previousGuide || nextGuide ? (
            <div className="xle-pager">
              {previousGuide ? (
                <a
                  href={hrefFor(previousGuide.path)}
                  onClick={(event) => handleAnchorNavigation(event, previousGuide.path)}
                  className="xle-card xle-pager-link"
                >
                  <small>{text.previous}</small>
                  <strong>{previousGuide.label}</strong>
                </a>
              ) : (
                <span />
              )}
              {nextGuide ? (
                <a
                  href={hrefFor(nextGuide.path)}
                  onClick={(event) => handleAnchorNavigation(event, nextGuide.path)}
                  className="xle-card xle-pager-link is-next"
                >
                  <small>{text.next}</small>
                  <strong>{nextGuide.label}</strong>
                </a>
              ) : null}
            </div>
          ) : null}
        </article>

        <aside className="xle-guide-aside">
          {page.facts?.length ? (
            <div className="xle-card xle-facts">
              <div className="xle-side-title">{text.facts}</div>
              <dl>
                {page.facts.map(([term, value]) => (
                  <div key={term}>
                    <dt>{term}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ) : null}
          {page.cta ? (
            <div className="xle-card xle-next-step">
              <strong>{text.nextStep}</strong>
              <p>{text.nextStepText}</p>
              <a
                href={hrefFor(page.cta.path)}
                onClick={(event) => handleAnchorNavigation(event, page.cta.path)}
                className="xle-btn is-go"
              >
                {page.cta.label} ›
              </a>
            </div>
          ) : null}
          {page.internalLinks.length > 0 ? (
            <div className="xle-card xle-links">
              <div className="xle-side-title">{text.related}</div>
              {page.internalLinks.map((link) => (
                <a
                  key={`${link.label}-${link.path}`}
                  href={hrefFor(link.path)}
                  onClick={(event) => handleAnchorNavigation(event, link.path)}
                >
                  <span>{link.label}</span>
                  <span aria-hidden="true">›</span>
                </a>
              ))}
            </div>
          ) : null}
        </aside>
      </div>
    </LearnShell>
  );
};

export const learnCanonicalUrl = canonicalLearnUrl;

export default LearnSeoPage;
